/* GurbetciCard Startseite, Verhalten der gc-Sections (Paket B6).

   Kein kundensichtbarer Text in dieser Datei: Beschriftungen, Muster und Fehlertexte
   kommen als data-Attribute aus der Section (docs/CONTENT.md Abschnitt 1 Nummer 6).

   Der Warenkorb nimmt beliebig viele Zeilen (Punkt 1.3). Die Einzeilen-Sperre ist weg:
   fremde Zeilen bleiben stehen, und dasselbe Paket ein zweites Mal gelegt zählt hoch,
   weil `/cart/add.js` von sich aus addiert. Jede Zeile trägt genau neun Properties
   (docs/DATA-MODEL.md §3.12) in zwei Gruppen mit getrennten Zwecken:

     sichtbar   drei Stück, Name und Wert lesbar und in der Sprache des Ladens.
                Der Name kommt aus dem Feldnamen der Section, der Ortsname aus der
                Ortsliste, das Datum als TT.MM.JJJJ aus dem ISO-Wert daneben.
     technisch  _destination, _region, _travel_start, _travel_end, _travel_days,
                _package. Führender Unterstrich, deshalb im Warenkorb und an der
                Kasse unsichtbar. Handle und ISO-Datum, weil gc-core und Odoo daran
                hängen. **Nur diese Gruppe liest gc-core.**

   Die Trennung ist der Punkt (Owner 02.09.2026): die sichtbaren Namen sind
   sprachabhängig und dürfen sich jederzeit ändern, ohne dass ein Ingest bricht.

   Die Reise wird als Zeitraum erfasst (Punkt 1.4). `_travel_days` ist kein Eingabefeld
   mehr, sondern die Zahl der Tage zwischen Beginn und Ende, An- und Abreisetag
   mitgezählt. Die Region kommt aus der Ortsliste, die `snippets/gc-orte.liquid` aus
   `tools/shopify/orte.json` spiegelt; in dieser Datei steht kein Ort und keine Region.

   Vor dem Checkout wird kein Dienst gerufen, es werden keine Empfängerdaten erhoben,
   nichts wird in localStorage oder in die Konsole geschrieben. */

import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const ENTPRELLUNG_MS = 300;
const SEKUNDEN_JE_TAG = 86400000;

/**
 * Liest eine Ganzzahl aus einem Feldwert.
 * @param {string} wert
 * @returns {number|null}
 */
function ganzzahl(wert) {
  const zahl = Number.parseInt(String(wert ?? '').trim(), 10);
  return Number.isFinite(zahl) ? zahl : null;
}


/** @returns {Date} heutiger Tag, lokale Zeitzone, ohne Uhrzeit */
function heute() {
  const jetzt = new Date();
  return new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
}

/**
 * Formatiert ein Datum als ISO-Tag in lokaler Zeit. `toISOString` scheidet aus,
 * weil es nach UTC verschiebt und damit je nach Zeitzone einen Tag daneben liegt.
 * @param {Date} datum
 * @returns {string}
 */
function alsIsoTag(datum) {
  const monat = String(datum.getMonth() + 1).padStart(2, '0');
  const tag = String(datum.getDate()).padStart(2, '0');
  return `${datum.getFullYear()}-${monat}-${tag}`;
}

/**
 * Liest einen ISO-Tag als lokales Datum.
 * @param {string} wert
 * @returns {Date|null}
 */
function ausIsoTag(wert) {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(wert ?? '').trim());
  if (!treffer) return null;
  const datum = new Date(Number(treffer[1]), Number(treffer[2]) - 1, Number(treffer[3]));
  return Number.isNaN(datum.getTime()) ? null : datum;
}

/**
 * Schreibt einen ISO-Tag als TT.MM.JJJJ, für den lesbaren Wert im Warenkorb.
 *
 * Gerechnet wird auf der Zeichenkette und nicht über `Intl`: Deutsch und Türkisch
 * schreiben dieses Datum gleich, `Intl` gäbe je nach Stil „10. Sept. 2026" oder eine
 * andere Reihenfolge, und der Wert steht am Ende in einer Bestellung. Ein Datum ohne
 * Uhrzeit soll außerdem keine Zeitzone durchlaufen.
 * @param {string} wert ISO-Tag
 * @returns {string} leer, wenn der Wert kein ISO-Tag ist
 */
function alsTagMonatJahr(wert) {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(wert ?? '').trim());
  return treffer ? `${treffer[3]}.${treffer[2]}.${treffer[1]}` : '';
}

/**
 * Schreibt einen ISO-Tag in der Schreibweise der Seitensprache — für **angezeigte** Daten.
 *
 * Der Unterschied zu `alsTagMonatJahr` ist der Zweck, nicht der Geschmack. Dort geht der
 * Wert in eine Bestellung und muss über alle zwanzig Sprachen derselbe sein. Hier steht er
 * nur auf der Seite, und dort ist `04.11.2026` ein Fehler: Wer englisch liest, versteht
 * den 11. April. Dasselbe gilt für die Fristzeile in jeder Sprache, die den Monat vorn
 * schreibt.
 *
 * Gerechnet wird auf der Zeichenkette und dann mit einem Datum in UTC — so läuft der Tag
 * durch keine Zeitzone und kann nicht um einen Tag springen.
 * @param {string} wert ISO-Tag
 * @returns {string} leer, wenn der Wert kein ISO-Tag ist
 */
function alsDatumInSeitensprache(wert) {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(wert ?? '').trim());
  if (!treffer) return '';
  const sprache = (document.documentElement.lang || '').trim();
  if (!sprache) return alsTagMonatJahr(wert);
  try {
    return new Intl.DateTimeFormat(sprache, {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    }).format(new Date(Date.UTC(Number(treffer[1]), Number(treffer[2]) - 1, Number(treffer[3]))));
  } catch (fehler) {
    // Eine unbekannte Sprachkennung darf die Fristzeile nicht leeren.
    return alsTagMonatJahr(wert);
  }
}

/**
 * Ersetzt Platzhalter in einem Muster aus einem Section-Setting.
 * @param {string} muster
 * @param {Record<string, string>} werte
 * @returns {string}
 */
function einsetzen(muster, werte) {
  let text = String(muster ?? '');
  for (const [platzhalter, wert] of Object.entries(werte)) {
    text = text.split(`{${platzhalter}}`).join(wert);
  }
  // Fehlt ein Wert, bleibt sonst eine leere Aufzählungsstelle stehen.
  return text.replace(/,\s*,/g, ',').replace(/\s{2,}/g, ' ').trim();
}

/**
 * @param {string} url
 * @param {object} rumpf
 * @returns {Promise<any>}
 */
async function sendeJson(url, rumpf) {
  const antwort = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(rumpf),
  });
  if (!antwort.ok) throw new Error(`Warenkorb antwortete mit ${antwort.status}`);
  return antwort.json();
}

/**
 * Baut die Kaufbox auf.
 * @param {HTMLElement} wurzel
 */
function kaufbox(wurzel) {
  if (wurzel.dataset.gcBereit === 'ja') return;
  wurzel.dataset.gcBereit = 'ja';

  /** @type {HTMLFormElement|null} */
  const formular = wurzel.querySelector('form');
  const felder = {
    id: wurzel.querySelector('[data-gc-id]'),
    paketfeld: wurzel.querySelector('[data-gc-paketfeld]'),
    ziel: wurzel.querySelector('[data-gc-ziel]'),
    region: wurzel.querySelector('[data-gc-region]'),
    start: wurzel.querySelector('[data-gc-start]'),
    ende: wurzel.querySelector('[data-gc-ende]'),
    tage: wurzel.querySelector('[data-gc-tage]'),
    menge: wurzel.querySelector('[data-gc-menge]'),
    sichtZiel: wurzel.querySelector('[data-gc-sicht-ziel]'),
    sichtStart: wurzel.querySelector('[data-gc-sicht-start]'),
    sichtEnde: wurzel.querySelector('[data-gc-sicht-ende]'),
  };
  if (!formular || !felder.start || !felder.ende || !felder.tage || !felder.menge) return;

  /* Die Pflichtfeldprüfung des Browsers wird erst hier abgeschaltet, nicht schon im
     Liquid (Befund P1, 02.09.2026). Vorher trug das Formular `novalidate`, und ohne
     Skript prüfte damit niemand: weder `kontrollieren()`, das es nicht gab, noch der
     Browser, dem es abgestellt war. Eine Bestellung ohne Reiseziel und ohne Datum
     wäre stillschweigend durchgegangen.

     Jetzt greift ohne Skript die Prüfung des Browsers an den Feldern mit `required`,
     und mit Skript diese Datei: sie muss `submit` selbst erreichen, um den eigenen
     Fehlertext neben das Feld zu stellen statt der Sprechblase des Browsers. Die
     Reihenfolge ist wichtig, denn der Browser prüft vor dem Ereignis `submit`. */
  formular.noValidate = true;

  // Ein Feld kann schon aus der Section eine Hilfszeile zugeordnet bekommen. Diese
  // Zuordnung wird gemerkt, damit ein Fehler sie ergänzt statt sie zu ersetzen.
  // `tage` steht nicht dabei: es ist seit Punkt 1.4 verborgen und gerechnet, ein
  // Fehler kann dort weder landen noch den Fokus bekommen.
  const pruefFelder = [felder.ziel, felder.start, felder.ende, felder.menge];
  for (const feld of pruefFelder) {
    if (feld) feld.dataset.gcBeschreibung = feld.getAttribute('aria-describedby') || '';
  }

  /* Der Satz „Daten-eSIM, keine lokale Rufnummer" steht auf der Startseite seit dem
     06.09.2026 als Häkchen im Hero und damit **außerhalb** der Kaufbox (Kürzung V-3): Er
     stand vorher zweimal auf der Seite, einmal hier unten und einmal oben. Auf der
     Produktseite gibt es kein Hero, dort steht er weiter im Kleingedruckten. Gesucht wird
     deshalb im ganzen Abschnitt und nicht nur in der Box. Gefunden wird in beiden Fällen
     dasselbe: das Element mit `data-gc-micro-daten`, das an der Tarifwahrheit `data_only`
     hängt. */
  const abschnitt = wurzel.closest('section') || document;

  const anzeigen = {
    chip: wurzel.querySelector('[data-gc-chip]'),
    notiz: wurzel.querySelector('[data-gc-notice]'),
    mikroDaten: abschnitt.querySelector('[data-gc-micro-daten]'),
    volumen: wurzel.querySelector('[data-gc-volumen]'),
    langreise: wurzel.querySelector('[data-gc-langreise]'),
    vorlauf: wurzel.querySelector('[data-gc-vorlauf]'),
    summeZeile: wurzel.querySelector('[data-gc-summe-zeile]'),
    summeStart: wurzel.querySelector('[data-gc-summe-start]'),
    summeTage: wurzel.querySelector('[data-gc-summe-tage]'),
    summePreis: wurzel.querySelector('[data-gc-summe-preis]'),
    status: wurzel.querySelector('[data-gc-status]'),
    fehler: wurzel.querySelector('[data-gc-fehler]'),
  };
  const knopf = wurzel.querySelector('[data-gc-knopf]');
  const kacheln = Array.from(wurzel.querySelectorAll('[data-gc-tile]'));
  const leiste = document.querySelector('[data-gc-sticky]');
  const leisteZeile = leiste ? leiste.querySelector('[data-gc-sticky-zeile]') : null;
  const leisteKnopf = leiste ? leiste.querySelector('[data-gc-sticky-knopf]') : null;

  const daten = wurzel.dataset;
  const grenzen = {
    menge: ganzzahl(daten.gcMaxMenge) ?? 20,
    // Vorlauf des **Reisebeginns** (DEC-065): höchstens 50 Tage. Sie schützt die
    // Einrichtungsfrist von 60 Tagen ab Kauf (DEC-067): 60 minus 50 lässt zehn Tage nach
    // der Ankunft, um die eSIM noch einzurichten.
    vorlauf: ganzzahl(daten.gcMaxVorlauf) ?? 50,
    // Grenze des **Reiseendes**, gerechnet ab dem Reisebeginn. Sie hat einen anderen Zweck:
    // reine Plausibilitaet gegen Tippfehler im Jahr. Sie darf nicht dieselbe sein wie der
    // Vorlauf — sonst könnte niemand mehr eine Reise buchen, die in 45 Tagen beginnt und
    // 30 Tage dauert (Befund der Gegenkontrolle vom 04.09.2026).
    // Rückfall, wenn das Attribut fehlt. Keine Sperre der Reisedauer, sondern eine
    // Plausibilitätsgrenze gegen den Tippfehler in der Jahreszahl: Die AGB kennt keine
    // Grenze der Reisedauer (Owner 05.09.2026, gemessen in allen zwanzig Sprachen).
    reisedauer: ganzzahl(daten.gcMaxReisedauer) ?? 365,
    // Ab wie vielen Tagen Abstand der Vorlauf-Hinweis erscheint. Keine Grenze:
    // gekauft werden kann bis `vorlauf` (Owner 04.09.2026, Hinweis statt Sperre).
    vorlaufHinweis: ganzzahl(daten.gcVorlaufHinweis) ?? 14,
  };
  const routen = {
    warenkorb: daten.gcRouteCart || '/cart',
    hinzu: daten.gcRouteCartAdd || '/cart/add',
  };
  const texte = {
    zielLeer: daten.gcFehlerZielLeer || '',
    datumLeer: daten.gcFehlerDatumLeer || '',
    datumVorlauf: daten.gcFehlerDatumVorlauf || '',
    endeLeer: daten.gcFehlerEndeLeer || '',
    endeVorStart: daten.gcFehlerEndeVorStart || '',
    endeZuSpaet: daten.gcFehlerEndeZuSpaet || daten.gcFehlerDatumVorlauf || '',
    menge: daten.gcFehlerMenge || '',
    warenkorb: daten.gcFehlerWarenkorb || '',
  };

  /* Ortsliste. Eine Wahrheit steht in `tools/shopify/orte.json`, ins Theme gespiegelt
     von `snippets/gc-orte.liquid`. Nachgeschlagen werden die Region für die technische
     Property und der Name für die sichtbare; beides kommt aus derselben Zeile, damit
     Anzeige und Auswertung nicht auseinanderlaufen. In dieser Datei steht weder ein
     Ortsname noch eine Region. Ist die Liste nicht lesbar, bleiben beide leer, und der
     Kauf läuft trotzdem: eine fehlende Zusatzangabe ist ein schlechter Grund, eine
     Bestellung anzuhalten. */
  const orteJeHandle = (function ortsKarte() {
    const karte = {};
    try {
      const liste = JSON.parse(daten.gcOrte || '[]');
      for (const ort of Array.isArray(liste) ? liste : []) {
        if (ort && ort.handle) {
          karte[String(ort.handle)] = {
            name: String(ort.name || ''),
            region: String(ort.region || ''),
          };
        }
      }
    } catch (fehler) {
      return {};
    }
    return karte;
  })();

  /**
   * Liest den Namen einer sichtbaren Property aus dem Feldnamen der Section.
   * Er steht dort und nirgends sonst, damit ein Absenden ohne Skript und eines mit
   * dieselbe Zeichenfolge schicken; in dieser Datei steht kein Wort davon.
   * @param {HTMLInputElement|null} feld
   * @returns {string} leer, wenn das Feld fehlt oder keinen Property-Namen trägt
   */
  function propertyName(feld) {
    const treffer = /^properties\[(.+)\]$/.exec(String((feld && feld.name) || ''));
    return treffer ? treffer[1] : '';
  }

  /* Mengenrabatt (DEC-044-Nachtrag, Befund PDK-2). Die Staffel steht als
     Theme-Setting an genau einer Stelle und kommt als data-Attribut aus der Section;
     in dieser Datei steht keine Schwelle, kein Prozentsatz und kein Text.

     Die Rundung ist am Warenkorb gemessen, nicht angenommen (31.08.2026, sieben Fälle
     über `tools/shopify/cart-rabattprobe.cjs`): Shopify zieht den Abzug **je Stück**
     ab und schneidet ihn dabei auf ganze Cent **ab**.
       2 × 6,99 → 1398 − 2 × 34 = 1330   (699 × 5 % = 34,95 → 34)
       2 × 18,99 → 3798 − 2 × 94 = 3610  (1899 × 5 % = 94,95 → 94)
       3 × 18,99 → 5697 − 3 × 284 = 4845 (1899 × 15 % = 284,85 → 284)
     Kaufmännisch gerundet käme 48,42 € heraus, die Kasse zieht 48,45 € ein. */
  const staffel = [
    { ab: ganzzahl(daten.gcRabattSchwelle1), prozent: ganzzahl(daten.gcRabattProzent1) },
    { ab: ganzzahl(daten.gcRabattSchwelle2), prozent: ganzzahl(daten.gcRabattProzent2) },
  ]
    .filter((stufe) => stufe.ab !== null && stufe.ab > 1 && stufe.prozent !== null && stufe.prozent > 0)
    .sort((a, b) => a.ab - b.ab);

  /**
   * @param {number} preis Stückpreis in kleinster Währungseinheit
   * @param {number} menge
   * @returns {number} Gesamtbetrag nach Mengenrabatt, in kleinster Währungseinheit
   */
  function summeRechnen(preis, menge) {
    let prozent = 0;
    for (const stufe of staffel) {
      if (menge >= stufe.ab) prozent = stufe.prozent;
    }
    if (prozent <= 0) return preis * menge;
    const abzugJeStueck = Math.floor((preis * prozent) / 100);
    return (preis - abzugJeStueck) * menge;
  }

  const sprache = daten.gcLocale || 'de-DE';
  const geldeinheit = daten.gcGeldeinheit || 'EUR';
  const geldformat = daten.gcGeldformat || '{{amount_with_comma_separator}}';
  const zahlenformat = new Intl.NumberFormat(sprache, { maximumFractionDigits: 1 });
  // Das Datum der Zusammenfassung schreibt die Sprache des Shops, nicht diese Datei.
  const datumsformat = new Intl.DateTimeFormat(sprache, { dateStyle: 'medium' });

  let inArbeit = false;
  let statusUhr = 0;

  /** @returns {HTMLInputElement|null} */
  function aktiveKachel() {
    return kacheln.find((kachel) => kachel.checked) ?? kacheln[0] ?? null;
  }

  /** @returns {Record<string, any>} Produktwahrheiten der gewählten Variante */
  function fakten() {
    const kachel = aktiveKachel();
    if (!kachel) return {};
    try {
      return JSON.parse(kachel.dataset.gcFakten || '{}') || {};
    } catch (fehler) {
      return {};
    }
  }

  /**
   * @param {number} betrag Preis in kleinster Währungseinheit
   * @returns {string}
   */
  function geld(betrag) {
    return formatMoney(betrag, geldformat, geldeinheit);
  }

  /** @param {HTMLElement|null} element @param {boolean} sichtbar */
  function zeige(element, sichtbar) {
    if (element) element.hidden = !sichtbar;
  }

  function fehlerLeeren() {
    if (anzeigen.fehler) anzeigen.fehler.textContent = '';
    for (const feld of pruefFelder) {
      if (!feld) continue;
      feld.removeAttribute('aria-invalid');
      const grund = feld.dataset.gcBeschreibung || '';
      if (grund) feld.setAttribute('aria-describedby', grund);
      else feld.removeAttribute('aria-describedby');
    }
  }

  /**
   * Klappt jeden geschlossenen Abschnitt über einem Element auf.
   *
   * Seit dem 06.09.2026 abends steht kein Feld der Kaufbox mehr in einem Klappelement:
   * Der Owner hat entschieden, dass Reiseziel, Zeitraum und Menge sichtbar sind. Diese
   * Funktion bleibt trotzdem stehen, und zwar mit Absicht. Ein Fehler darf nie auf ein
   * Feld zeigen, das niemand sieht; käme je wieder ein Klappelement dazu, griffe die
   * Regel ohne Zutun. Heute findet sie nichts und kostet nichts.
   *
   * `while` und nicht `if`: Kommt eines Tages ein zweiter Abschnitt dazu, greift die
   * Regel weiter.
   * @param {HTMLElement|null} element
   */
  function abschnittOeffnen(element) {
    let block = element ? element.closest('details') : null;
    while (block) {
      block.open = true;
      block = block.parentElement ? block.parentElement.closest('details') : null;
    }
  }

  /**
   * @param {string} text
   * @param {HTMLElement|null} feld
   */
  function fehlerZeigen(text, feld) {
    if (anzeigen.fehler) anzeigen.fehler.textContent = text;
    abschnittOeffnen(feld);
    if (feld && anzeigen.fehler && anzeigen.fehler.id) {
      feld.setAttribute('aria-invalid', 'true');
      const grund = feld.dataset.gcBeschreibung || '';
      feld.setAttribute('aria-describedby', grund ? `${grund} ${anzeigen.fehler.id}` : anzeigen.fehler.id);
      feld.focus();
    }
  }

  /** @returns {number} Anzahl im erlaubten Bereich, nur für die Anzeige */
  function mengeAnzeige() {
    const wert = ganzzahl(felder.menge.value) ?? 1;
    return Math.min(Math.max(wert, 1), grenzen.menge);
  }

  /**
   * Rechnet die Reisetage aus Beginn und Ende und schreibt sie in das verborgene Feld
   * `properties[travel_days]` (Punkt 1.4). An- und Abreisetag zählen beide mit:
   * Ende minus Beginn, plus eins. Ein Tag mit 23 oder 25 Stunden (Zeitumstellung)
   * liefert deshalb keinen halben Tag, sondern wird gerundet.
   *
   * Fehlt eines der beiden Daten oder liegt das Ende vor dem Beginn, bleibt das Feld
   * leer: lieber keine Zahl als eine falsche im Warenkorb. Den Fehlertext gibt
   * `kontrollieren()`.
   * @returns {number|null}
   */
  function tageRechnen() {
    const start = ausIsoTag(felder.start.value);
    const ende = ausIsoTag(felder.ende.value);
    if (!start || !ende) {
      felder.tage.value = '';
      return null;
    }
    const tage = Math.round((ende.getTime() - start.getTime()) / SEKUNDEN_JE_TAG) + 1;
    felder.tage.value = tage >= 1 ? String(tage) : '';
    return tage;
  }

  /** @returns {string} Volumenzeile, leer wenn die Ableitung nicht trägt */
  function volumenText() {
    if (!anzeigen.volumen) return '';
    const gb = Number(fakten().volume_gb);
    const tage = ganzzahl(felder.tage.value);
    if (!Number.isFinite(gb) || gb <= 0 || tage === null || tage < 1) return '';
    return einsetzen(anzeigen.volumen.dataset.gcMuster, { gb: zahlenformat.format(gb / tage) });
  }

  function statusAngleichen(paket, menge, volumen, summe) {
    if (!anzeigen.status) return;
    const muster =
      menge === 1 ? anzeigen.status.dataset.gcMusterEinzahl : anzeigen.status.dataset.gcMusterMehrzahl;
    const satz = einsetzen(muster, { anzahl: String(menge), paket, volumen, summe });
    window.clearTimeout(statusUhr);
    statusUhr = window.setTimeout(() => {
      if (anzeigen.status) anzeigen.status.textContent = satz;
    }, ENTPRELLUNG_MS);
  }

  /**
   * Zeigt den Hinweis für lange Reisen (Punkt 1.4). Er sperrt nichts: eine Reise, die
   * länger dauert als die Laufzeit des Pakets, ist kein Fehler, sondern der Fall für
   * ein zweites Paket. Die Laufzeit kommt aus der Produktwahrheit, der Wortlaut aus
   * dem Section-Setting.
   */
  function langreiseAngleichen() {
    if (!anzeigen.langreise) return;
    const laufzeit = ganzzahl(fakten().validity_days);
    const tage = ganzzahl(felder.tage.value);
    const zuLang = laufzeit !== null && tage !== null && tage > laufzeit;
    if (zuLang) {
      anzeigen.langreise.textContent = einsetzen(anzeigen.langreise.dataset.gcMuster, {
        tage: String(laufzeit),
      });
    }
    zeige(anzeigen.langreise, zuLang);
  }

  /**
   * Der Hinweis bei weit entferntem Reisebeginn. Er sperrt nichts.
   *
   * Der Owner hat ihn am 04.09.2026 ausdrücklich als Hinweis und nicht als Sperre
   * verlangt: „Kann ja auch sein, dass er es früher an jemand anderen weitergibt oder
   * verschenkt." Die harte Grenze bleibt `grenzen.vorlauf` (50 Tage, DEC-065) und wird
   * weiter über `felder.start.max` und `kontrollieren()` durchgesetzt.
   *
   * Das Fristdatum rechnet der Browser aus dem heutigen Tag, nicht Liquid: die Seite
   * liegt im Cache, ein dort eingesetztes Datum wäre am nächsten Tag falsch. Gezeigt
   * wird es in der Sprache des Ladens (`datumsformat`), weil dieser Text nur gelesen
   * und nirgends weiterverarbeitet wird.
   */
  function vorlaufAngleichen() {
    if (!anzeigen.vorlauf) return;
    const start = ausIsoTag(felder.start ? felder.start.value : '');
    const abstand = start === null
      ? null
      : Math.round((start.getTime() - heute().getTime()) / SEKUNDEN_JE_TAG);
    const weitWeg = abstand !== null && abstand > grenzen.vorlaufHinweis;
    if (weitWeg) {
      const fristTage = ganzzahl(daten.gcFristTage) ?? 60;
      const frist = new Date(heute().getTime() + fristTage * SEKUNDEN_JE_TAG);
      anzeigen.vorlauf.textContent = einsetzen(anzeigen.vorlauf.dataset.gcMuster, {
        tage: String(abstand),
        datum: datumsformat.format(frist),
      });
    }
    zeige(anzeigen.vorlauf, weitWeg);
  }

  function zeichnen() {
    const kachel = aktiveKachel();
    if (!kachel) return;
    tageRechnen();
    const werte = fakten();
    const menge = mengeAnzeige();
    const paket = kachel.dataset.gcPaketname || '';
    const preis = ganzzahl(kachel.dataset.gcPreis) ?? 0;
    const summe = geld(summeRechnen(preis, menge));

    /* Beide Felder gibt es nur auf der Produktseite, wo ein einziges Paket feststeht.
       In der Kaufbox der Startseite trägt der Radio den Feldnamen `id` selbst und es
       gibt kein verborgenes Paketfeld mehr (Befund P1): dort ist hier nichts zu tun,
       und die Abfragen laufen ins Leere statt einen falschen Wert zu setzen. */
    if (felder.id) felder.id.value = kachel.value;
    if (felder.paketfeld) felder.paketfeld.value = kachel.dataset.gcPaketcode || '';

    /* Der Verweis auf die Vertragszusammenfassung nach § 54 TKG muss auf das Paket zeigen,
       das gerade gekauft wird — die Zusammenfassung beschreibt einen konkreten Vertrag mit
       Volumen und Preis. Auf der Produktseite steht das Paket fest, hier wechselt es mit
       der Kachel. Die vier Adressen liefert `gc-tkg-link.liquid` als Datenfelder, weil ein
       selbst gebauter Pfad das Sprachpräfix verlöre. Fehlt eine, bleibt der bisherige
       Verweis stehen statt ins Leere zu zeigen. */
    const tkgLink = wurzel.querySelector('[data-gc-tkg-link]');
    if (tkgLink) {
      const stufe = String(kachel.dataset.gcPaketcode || '').replace('GC-TR-', '');
      const ziel = tkgLink.dataset['gcTkg' + stufe];
      if (ziel) tkgLink.setAttribute('href', ziel);
    }

    if (anzeigen.chip) {
      const tage = ganzzahl(werte.validity_days);
      zeige(anzeigen.chip, tage !== null);
      if (tage !== null) {
        anzeigen.chip.textContent = einsetzen(anzeigen.chip.dataset.gcMuster, { tage: String(tage) });
      }
    }
    zeige(anzeigen.notiz, Boolean(werte.activation_policy));
    zeige(anzeigen.mikroDaten, werte.data_only === true);

    langreiseAngleichen();
    vorlaufAngleichen();

    const volumen = volumenText();
    if (anzeigen.volumen) {
      anzeigen.volumen.textContent = volumen;
      zeige(anzeigen.volumen, volumen !== '');
    }

    if (anzeigen.summeZeile) {
      anzeigen.summeZeile.textContent = einsetzen(anzeigen.summeZeile.dataset.gcMuster, {
        anzahl: String(menge),
        paket,
      });
    }
    if (anzeigen.summeStart) {
      const start = ausIsoTag(felder.start.value);
      anzeigen.summeStart.textContent = start
        ? einsetzen(anzeigen.summeStart.dataset.gcMuster, { start: datumsformat.format(start) })
        : '';
    }
    if (anzeigen.summeTage) {
      const tage = ganzzahl(felder.tage.value);
      const muster =
        tage === 1 ? anzeigen.summeTage.dataset.gcMusterEinzahl : anzeigen.summeTage.dataset.gcMusterMehrzahl;
      anzeigen.summeTage.textContent =
        tage !== null && tage >= 1 ? einsetzen(muster, { tage: String(tage) }) : '';
    }
    if (anzeigen.summePreis) anzeigen.summePreis.textContent = summe;
    if (leisteZeile) {
      leisteZeile.textContent = einsetzen(leisteZeile.dataset.gcMuster, { paket, summe });
    }
    statusAngleichen(paket, menge, volumen, summe);
  }

  /**
   * Geprüft wird in der Reihenfolge der Felder im Formular, damit der Fokus nach vorn
   * springt statt zu hüpfen.
   *
   * Ein Reisebeginn in der Vergangenheit ist seit Punkt 1.4 **erlaubt**: wer schon in
   * Türkiye ist und dort erst merkt, dass er Daten braucht, soll kaufen können. Eine
   * zu lange Reise ist ebenfalls kein Fehler mehr, sie bekommt nur den Hinweis aus
   * `langreiseAngleichen()`.
   * @returns {{feld: HTMLElement, text: string}|null}
   */
  function kontrollieren() {
    if (felder.ziel && String(felder.ziel.value || '').trim() === '') {
      return { feld: felder.ziel, text: texte.zielLeer };
    }

    const start = ausIsoTag(felder.start.value);
    if (!start) return { feld: felder.start, text: texte.datumLeer };

    const letzter = new Date(heute().getTime() + grenzen.vorlauf * SEKUNDEN_JE_TAG);
    if (start.getTime() > letzter.getTime()) return { feld: felder.start, text: texte.datumVorlauf };

    const ende = ausIsoTag(felder.ende.value);
    if (!ende) return { feld: felder.ende, text: texte.endeLeer };
    if (ende.getTime() < start.getTime()) return { feld: felder.ende, text: texte.endeVorStart };
    // Das Ende wird gegen den **Beginn** gemessen, nicht gegen heute: Sonst haette eine
    // Reise, die kurz vor der Vorlaufgrenze beginnt, keine Dauer mehr.
    const spaetestesEnde = new Date(start.getTime() + grenzen.reisedauer * SEKUNDEN_JE_TAG);
    if (ende.getTime() > spaetestesEnde.getTime()) {
      return { feld: felder.ende, text: texte.endeZuSpaet };
    }

    const menge = ganzzahl(felder.menge.value);
    if (menge !== null && menge > grenzen.menge) return { feld: felder.menge, text: texte.menge };

    return null;
  }

  /** @returns {string} Abschnitts-Kennungen des Warenkorbs für die Neuausgabe */
  function warenkorbAbschnitte() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
  }

  async function warenkorbLesen() {
    const antwort = await fetch(`${routen.warenkorb}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!antwort.ok) throw new Error(`Warenkorb antwortete mit ${antwort.status}`);
    return antwort.json();
  }

  /**
   * Liest den Warenkorb, ohne den Ablauf zu beenden. Ein Lesevorgang ändert nichts;
   * scheitert er, ist die Ware trotzdem abgelegt, und der Kunde darf dafür keinen
   * Fehlertext bekommen.
   * @returns {Promise<any|null>}
   */
  async function standLesen() {
    try {
      return await warenkorbLesen();
    } catch (fehler) {
      return null;
    }
  }

  /**
   * @param {any} beleg Antwort des Warenkorbs, aus der das Ereignis seinen Stand baut.
   * @returns {any|null} null, wenn die Antwort keine Warenkorbform hat
   */
  function alsWarenkorb(beleg) {
    try {
      return CartLinesUpdateEvent.createCartFromAjaxResponse(beleg);
    } catch (fehler) {
      return null;
    }
  }

  function knopfSperren() {
    if (!knopf) return;
    knopf.dataset.gcLabel = knopf.textContent || '';
    knopf.disabled = true;
    if (knopf.dataset.gcWarte) knopf.textContent = knopf.dataset.gcWarte;
  }

  function knopfFreigeben() {
    if (!knopf) return;
    knopf.disabled = false;
    if (knopf.dataset.gcLabel) knopf.textContent = knopf.dataset.gcLabel;
  }

  async function ablegen() {
    const kachel = aktiveKachel();
    if (!kachel) return;
    const menge = mengeAnzeige();
    const ziel = String(felder.ziel ? felder.ziel.value : '').trim();
    const start = String(felder.start.value || '').trim();
    const ende = String(felder.ende.value || '').trim();
    // Region und Ortsname werden nachgeschlagen, nie getippt. Ein unbekannter Ort
    // liefert leere Werte und hält den Kauf trotzdem nicht auf.
    const ort = orteJeHandle[ziel] || { name: '', region: '' };

    /* Neun Properties in zwei Gruppen (Owner 02.09.2026).

       Zuerst die drei sichtbaren: Shopify zeigt sie im Warenkorb und an der Kasse mit
       ihrem Namen als Beschriftung, deshalb tragen sie lesbare Namen und lesbare Werte
       in der Sprache des Ladens. Sie stehen vorn, weil der Warenkorb sie in dieser
       Reihenfolge untereinander schreibt.

       Danach die sechs technischen mit führendem Unterstrich. Die blendet Shopify
       überall aus, liefert sie aber unverändert an die Bestellung. Sie behalten Handle
       und ISO-Datum, weil gc-core und Odoo daran hängen, und sie sind die einzige
       Gruppe, die gc-core liest: die sichtbaren Namen dürfen sich mit der Sprache
       ändern, ohne dass ein Ingest bricht.

       Beide Gruppen kommen aus derselben Quelle. Das Datum steht nicht zweimal erfasst
       da, sondern einmal als ISO-Wert und einmal daraus geschrieben. */
    const eigenschaften = {};
    const sichtbar = [
      [felder.sichtZiel, ort.name],
      [felder.sichtStart, alsTagMonatJahr(start)],
      [felder.sichtEnde, alsTagMonatJahr(ende)],
    ];
    for (const [feld, wert] of sichtbar) {
      const name = propertyName(feld);
      // Ohne Namen gibt es keine Property, und ein leerer Wert stünde im Warenkorb als
      // Zeile ohne Aussage. Beides wird ausgelassen statt halb geschrieben.
      if (feld) feld.value = wert;
      if (name && wert) eigenschaften[name] = wert;
    }

    if (felder.region) felder.region.value = ort.region;
    eigenschaften._destination = ziel;
    eigenschaften._region = ort.region;
    eigenschaften._travel_start = start;
    eigenschaften._travel_end = ende;
    eigenschaften._travel_days = String(ganzzahl(felder.tage.value) ?? '');
    eigenschaften._package = String(kachel.dataset.gcPaketcode || '');

    const abschnitte = warenkorbAbschnitte();
    const auftrag = { id: Number(kachel.value), quantity: menge, properties: eigenschaften };

    const zugesagt = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(kachel.value), quantity: menge }],
        promise: zugesagt.promise,
      })
    );

    try {
      /* Ein einziger Schreibvorgang. Bis Punkt 1.3 folgten auf das Hinzufügen zwei
         weitere Aufrufe: eine Schleife, die jede fremde Zeile auf 0 setzte, und ein
         `cart/change`, das die Menge absolut nachzog. Beides ist ersatzlos weg.
         `/cart/add.js` addiert von sich aus auf eine bestehende Zeile mit derselben
         Variante und denselben Properties, und genau das ist jetzt gewollt: 5 GB und
         danach 20 GB stehen als zwei Zeilen im Korb, zweimal 5 GB als Menge 2.
         Die Abschnitte für die Schublade kommen deshalb aus diesem Aufruf. */
      const antwort = await sendeJson(`${routen.hinzu}.js`, {
        items: [auftrag],
        ...(abschnitte ? { sections: abschnitte, sections_url: window.location.pathname } : {}),
      });

      // Ab hier liegt die Ware im Warenkorb. Was jetzt noch fehlschlägt, ist ein Lesevorgang,
      // und der wird mit dem besten bekannten Stand aufgelöst statt als Fehler gezeigt.
      const beleg = (await standLesen()) ?? antwort;
      zugesagt.resolve({
        cart: alsWarenkorb(beleg),
        detail: {
          items: beleg.items,
          itemCount: menge,
          sections: antwort.sections,
          source: 'gc-hero-buybox',
          didError: false,
        },
      });
      // Horizon öffnet die Schublade nur, wenn `cart-drawer-component` das Attribut
      // `auto-open` trägt (assets/cart-drawer.js, gespeist aus settings.auto_open_cart_drawer).
      // Fehlt das Attribut, bleibt der Weg auf die Warenkorbseite (docs/DESIGN-SYSTEM.md §5).
      if (!document.querySelector('cart-drawer-component[auto-open]')) {
        window.location.href = routen.warenkorb;
      }
    } catch (fehler) {
      zugesagt.reject(fehler);
      throw fehler;
    }
  }

  for (const kachel of kacheln) {
    kachel.addEventListener('change', () => {
      zeichnen();
    });
  }


  felder.menge.addEventListener('input', () => zeichnen());
  felder.start.addEventListener('input', () => zeichnen());
  felder.ende.addEventListener('input', () => zeichnen());
  if (felder.ziel) felder.ziel.addEventListener('change', () => zeichnen());

  /**
   * @param {HTMLInputElement} feld
   * @param {number} schritt
   */
  function stufen(feld, schritt) {
    const unten = ganzzahl(feld.min) ?? 1;
    const oben = ganzzahl(feld.max) ?? Number.MAX_SAFE_INTEGER;
    const gelesen = ganzzahl(feld.value);
    // Aus einem leeren Feld führt der erste Schritt nach oben auf die Untergrenze.
    const jetzt = gelesen ?? (schritt > 0 ? unten - schritt : unten);
    const neu = Math.min(Math.max(jetzt + schritt, unten), oben);
    feld.value = String(neu);
    zeichnen();
  }

  // Nur noch die Anzahl hat einen Zähler. Den für die Reisedauer hat das Reiseende
  // abgelöst (Punkt 1.4).
  const stufenTasten = [
    ['[data-gc-menge-minus]', felder.menge, -1],
    ['[data-gc-menge-plus]', felder.menge, 1],
  ];
  for (const [wahl, feld, schritt] of stufenTasten) {
    const element = wurzel.querySelector(wahl);
    if (element) element.addEventListener('click', () => stufen(feld, schritt));
  }

  formular.addEventListener('submit', async (ereignis) => {
    ereignis.preventDefault();
    if (inArbeit) return;


    fehlerLeeren();
    const befund = kontrollieren();
    if (befund) {
      fehlerZeigen(befund.text, befund.feld);
      return;
    }

    inArbeit = true;
    knopfSperren();
    try {
      await ablegen();
    } catch (fehler) {
      fehlerZeigen(texte.warenkorb, null);
    } finally {
      inArbeit = false;
      knopfFreigeben();
    }
  });

  // Datumsgrenzen erst im Browser setzen: `{{ 'now' | date }}` friert mit dem Seiten-Cache ein.
  // Gesetzt wird nur die obere Grenze. Eine untere gibt es seit Punkt 1.4 nicht mehr:
  // wer schon in Türkiye ist, trägt einen Reisebeginn in der Vergangenheit ein.
  const letzterTag = alsIsoTag(new Date(heute().getTime() + grenzen.vorlauf * SEKUNDEN_JE_TAG));
  felder.start.max = letzterTag;

  /**
   * Führt die obere Grenze des Endedatums am gewählten Beginn nach.
   *
   * Vorher stand hier ein einziger, beim Laden gerechneter Wert: spätester Beginn plus
   * längste Reisedauer. Gemessen am 05.09.2026 bot der Kalender damit Reiseenden bis
   * `heute + 140` an, während `kontrollieren()` ab `Beginn + 90` ablehnte — bei jedem
   * Beginn ausser dem spätestmöglichen bot der Wähler bis zu fünfzig Tage an, die er
   * danach zurückwies. Ein Kalender, der anbietet, was die Prüfung ablehnt, ist kein
   * Angebot, sondern eine Falle.
   *
   * Jetzt rechnet die Grenze mit dem tatsächlich gewählten Beginn. Solange keiner
   * gewählt ist, bleibt der weite Rahmen stehen, sonst wäre das Feld vor der ersten
   * Eingabe gesperrt.
   */
  const endeGrenzeNachfuehren = () => {
    const gewaehlt = ausIsoTag(felder.start.value);
    const anker = gewaehlt
      ? gewaehlt.getTime()
      : heute().getTime() + grenzen.vorlauf * SEKUNDEN_JE_TAG;
    felder.ende.max = alsIsoTag(new Date(anker + grenzen.reisedauer * SEKUNDEN_JE_TAG));
  };
  endeGrenzeNachfuehren();
  felder.start.addEventListener('input', endeGrenzeNachfuehren);

  /* Die Frist als Datum, nicht als Tageszahl (DEC-065). „gültig bis 03.11.2026" ist prüfbar,
     „60 Tage" muss die Kundin selbst rechnen — und rechnet ab dem falschen Tag. Der Satz im
     Liquid ist der Rückfall ohne Skript und nennt die Zahl; hier wird er durch das Muster mit
     dem gerechneten Datum ersetzt.

     **Gerechnet wird ab dem Kauftag, nicht ab dem Reisebeginn** (DEC-067 ausdrücklich:
     „nicht die Anzeigen in allen Sprachen vom Reisedatum abhängig machen"). Die Reisedaten
     stehen zwei Felder weiter oben und werden hier bewusst nicht angefasst.

     Warum im Browser und nicht in Liquid: `{{ 'now' | date }}` friert mit dem Seiten-Cache ein,
     dieselbe Falle wie bei den Datumsgrenzen darüber. Gerechnet wird auf dem Kalendertag, damit
     die Anzeige zum Ablauf passt — der endet seit dem 04.09.2026 am Ende des deutschen
     Kalendertages (gc-core `tokens.ablauf_berechnen`).

     Der Rest, den diese Zeile nicht auflöst: Wer die Seite um 23:59 öffnet und um 00:01 kauft,
     sieht ein Datum, das einen Tag zu früh liegt. Die Richtung ist die sichere — die Anzeige
     warnt strenger als die Regel —, und die verbindliche Zahl steht in der Bestellbestätigung
     und im Kundenkonto. */
  /* Geschrieben wird seit dem 06.09.2026 in `[data-gc-frist]` und nicht mehr in
     `.gc-frist`: In demselben Kasten steht jetzt als zweite Zeile der Bedienhinweis, der
     vorher einen eigenen Rahmen hatte (Kürzung V-2). Wer den ganzen Kasten überschreibt,
     nimmt diesen Satz mit. */
  const fristFeld = wurzel.querySelector('[data-gc-frist]');
  const fristMuster = daten.gcFristMuster || '';
  const fristTage = ganzzahl(daten.gcFristTage) ?? 60;
  if (fristFeld && fristMuster.indexOf('{datum}') !== -1) {
    const fristTag = alsIsoTag(new Date(heute().getTime() + fristTage * SEKUNDEN_JE_TAG));
    fristFeld.textContent = einsetzen(fristMuster, {datum: alsDatumInSeitensprache(fristTag)});
  }

  /**
   * Bringt die Kaufbox ins Bild. Ohne Skript trägt die Sprungmarke `#gc-kaufbox`
   * dieselbe Bewegung, nur ohne Weichzeichnung.
   * @param {ScrollLogicalPosition} ausrichtung
   */
  function zurKaufbox(ausrichtung) {
    const sanft = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    wurzel.scrollIntoView({ behavior: sanft ? 'smooth' : 'auto', block: ausrichtung });
  }

  if (leiste && leisteKnopf) {
    leisteKnopf.addEventListener('click', (ereignis) => {
      ereignis.preventDefault();
      zurKaufbox('center');
      const ziel = knopf;
      if (ziel) ziel.focus({ preventScroll: true });
    });

    if ('IntersectionObserver' in window) {
      /* Beobachtet wird seit dem 06.09.2026 der **Kaufknopf** und nicht mehr die ganze
         Kaufbox (Kürzung V-6, Befund B-3).

         Gemessen am 05.09.2026: Auf allen fünf Mobilbreiten und in beiden Sprachen stand
         im ersten Bildschirm genau ein klickbares Element mit Text — das Warenkorbsymbol.
         Das ist kein Kaufweg. Die Leiste hätte geholfen, war aber verborgen, solange
         irgendein Teil der Kaufbox im Bild war; und die Box beginnt bei 375 px schon bei
         447 px. Zwischen Seitenanfang und dem Erscheinen der Leiste hatte die Kundin
         keinen Kaufweg im Bild, außer sie rollte bis 1 555 px.

         Am Knopf gemessen kehrt sich das um: Die Leiste steht ab dem ersten Bildschirm
         und verschwindet genau dann, wenn der echte Kaufknopf im Bild ist. Damit stehen
         nie zwei Kauf-CTA gleichzeitig da — §47 des Masterprompts bleibt erfüllt, und er
         war der Grund, warum die Leiste überhaupt versteckt wurde.

         Fehlt der Knopf (gesperrter Markt), gibt es auch keine Leiste; dann bleibt die
         Box der Bezug, und der Zweig läuft wie vorher. */
      const beobachtet = knopf || wurzel;
      const beobachter = new IntersectionObserver(
        (liste) => {
          for (const eintrag of liste) {
            leiste.classList.toggle('is-visible', !eintrag.isIntersecting);
          }
        },
        { threshold: 0 }
      );
      beobachter.observe(beobachtet);
    }
  }

  // Aufrufe aus anderen Abschnitten (Familie, Tarifkarten) stellen Paket und Anzahl
  // in der Kaufbox ein und bringen sie anschließend ins Bild. Ohne aktives Skript
  // trägt die Sprungmarke `#gc-kaufbox` denselben Weg, nur ohne Vorauswahl. In den
  // Attributen stehen ausschließlich Zahlen, kein Text.
  document.addEventListener('click', (ereignis) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;
    const verweis = ziel.closest('a[data-gc-select], a[data-gc-qty-set]');
    if (!verweis) return;

    ereignis.preventDefault();

    const nr = ganzzahl(verweis.getAttribute('data-gc-select'));
    if (nr !== null && kacheln[nr] instanceof HTMLInputElement) {
      kacheln[nr].checked = true;
    }

    const menge = ganzzahl(verweis.getAttribute('data-gc-qty-set'));
    if (menge !== null) {
      felder.menge.value = String(Math.min(Math.max(menge, 1), grenzen.menge));
    }

    // Die Vorlesezone wird zuerst geleert: sonst schweigt sie, wenn der neue Satz
    // Wort für Wort dem alten gleicht, und die Umstellung bliebe unhörbar.
    // („Region" heißt hier die Vorlesezone, nicht die Reiseregion aus der Ortsliste.)
    if (anzeigen.status) anzeigen.status.textContent = '';
    zeichnen();

    zurKaufbox('start');
    const gewaehlt = aktiveKachel();
    if (gewaehlt) gewaehlt.focus({ preventScroll: true });
  });


  zeichnen();
}

function starten() {
  for (const wurzel of document.querySelectorAll('[data-gc-buybox]')) {
    if (wurzel instanceof HTMLElement) kaufbox(wurzel);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starten, { once: true });
} else {
  starten();
}

// Der Theme-Editor baut Abschnitte neu auf; danach muss die Kaufbox erneut binden.
document.addEventListener('shopify:section:load', starten);
